import { motion } from "framer-motion";
import styled, { css } from "styled-components";
import { Button } from "../../components/Buttons/styles";
import {
  BORDER_COLORS,
  COLORS,
  FIELD_COLORS,
  ICON_COLORS,
  LAYER_COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { FONT_CSS, FONT_SIZE } from "../../constants/design/fontSize";
import {
  BORDER_RADIUS,
  COMPONENTS,
  SPACING,
} from "../../constants/design/spacing";
import { TableWrapper } from "../../components/shared/Table/style";

export const Container = styled(motion.div)<{ $isMobileScreen?: boolean }>`
  padding-top: ${({ $isMobileScreen }) =>
    $isMobileScreen ? SPACING.two : SPACING.five}px;
  padding-bottom: ${({ $isMobileScreen }) =>
    $isMobileScreen ? SPACING.three : SPACING.five}px;
`;

export const ReferralStatsWrapper = styled.div<{ $isMobileScreen?: boolean }>`
  margin-top: ${SPACING.four}px;
  width: 100%;
  display: grid;
  ${({ $isMobileScreen }) =>
    $isMobileScreen
      ? css`
          grid-template-columns: repeat(2, 1fr);
          gap: ${SPACING.four}px ${SPACING.three}px;
        `
      : css`
          grid-template-columns: repeat(4, 1fr);
        `};
`;

export const ReferralStat = styled.div`
  > span {
    color: ${TEXT_COLORS.three};
    ${FONT_CSS.label.three}
  }

  > p {
    margin-top: ${SPACING.two}px;
    ${FONT_CSS.data.two}
  }
`;

export const ReferralLinkWrapper = styled.div<{ $isMobileScreen?: boolean }>`
  margin-top: ${SPACING.four}px;
  display: grid;
  grid-gap: ${SPACING.three}px;

  ${({ $isMobileScreen }) =>
    $isMobileScreen
      ? css`
          grid-template-columns: 1fr;
          grid-template-rows: 1fr 1fr;
        `
      : css`
          grid-template-columns: 1fr 160px;

          > ${Button} {
            width: 160px;
            display: inline-block;
          }
        `}
  align-items: center;
`;

export const ReferralLink = styled.div`
  display: flex;
  border-radius: ${BORDER_RADIUS.five}px;
  justify-content: space-between;
  gap: ${SPACING.two}px;
  padding: ${SPACING.two}px;
  background-color: ${FIELD_COLORS.one};
  font-size: ${FONT_SIZE.two};

  > * {
    margin: auto 0;
  }

  > p {
    color: ${TEXT_COLORS.three};
    min-width: 0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    > span {
      color: ${TEXT_COLORS.one};
    }
  }

  > button {
    border: 1px solid transparent;
    width: 32px;
    height: 32px;
    flex: 0 0 32px;
    background-color: ${FIELD_COLORS.two};
    border-radius: ${BORDER_RADIUS.two}px;
    color: ${TEXT_COLORS.one};
    transition: 0.2s all ease-in-out;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:hover {
      border: 1px solid ${BORDER_COLORS.five};
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

export const ReferralUsernameEditForm = styled.form`
  display: flex;
  flex: 1;
  align-items: center;
  gap: ${SPACING.one}px;
  min-width: 0;
`;

export const ReferralUsernameInput = styled.input`
  flex: 1;
  min-width: 0;
  font-size: ${FONT_SIZE.two};
  color: ${TEXT_COLORS.one};
  background: ${FIELD_COLORS.two};
  border: 1px solid ${BORDER_COLORS.five};
  border-radius: ${BORDER_RADIUS.two}px;
  padding: ${SPACING.one}px ${SPACING.two}px;
  outline: none;

  &:focus {
    border-color: ${COLORS.highlight.one};
  }
`;

export const TableContainer = styled.div`
  margin-top: ${SPACING.five}px;
`;

export const EmptyContent = styled.div`
  padding-top: ${SPACING.four}px;
  color: ${TEXT_COLORS.two};
  font-size: ${FONT_SIZE.two};
`;

export const RewardIcon = styled.div`
  width: 32px;
  height: 32px;
  background-color: ${COLORS.neutral.six};
  margin-right: ${SPACING.two}px;
  border-radius: 50%;
  display: flex;

  > svg {
    margin: auto;
    stroke: ${ICON_COLORS.one};
  }
`;

export const TraderWrapper = styled.div`
  display: flex;
  flex-direction: column;

  > div:first-of-type {
    color: ${TEXT_COLORS.one};
  }
`;

export const RewardReferralTableWrapper = styled(TableWrapper)`
  table {
    margin-top: ${SPACING.two}px;
    border-collapse: separate;
    border-spacing: 0;

    thead {
      tr {
        height: 100%;
      }
      border-bottom: ${SPACING.two}px solid transparent;
    }

    th {
      background-color: transparent;
    }

    tbody {
      tr {
        height: ${COMPONENTS.portfolioTableRow}px;
        border: none;
        background-color: transparent;
        cursor: pointer;
      }
      td {
        background-color: ${LAYER_COLORS.one};
        &:first-child {
          border-top-left-radius: ${BORDER_RADIUS.md}px;
          border-bottom-left-radius: ${BORDER_RADIUS.md}px;
        }
        &:last-child {
          border-top-right-radius: ${BORDER_RADIUS.md}px;
          border-bottom-right-radius: ${BORDER_RADIUS.md}px;
        }
      }
    }
  }
`;

export const VideoContainer = styled.div<{ isMobile?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: -100;
  height: ${({ isMobile }) => (isMobile ? 120 : 240)}px;
  border-radius: 12px;
  overflow: hidden;
  background: ${LAYER_COLORS.four};
  margin-bottom: ${({ isMobile }) => (isMobile ? SPACING.three : 0)}px;

  video {
    opacity: 0.12;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const HeaderTitle = styled.div`
  ${FONT_CSS.header.six};
  color: ${TEXT_COLORS.one};
  margin: 0;
  margin-bottom: ${SPACING.two}px;
`;

export const HeaderContainer = styled.div<{ $isMobileScreen?: boolean }>`
  position: relative;
  z-index: 1;

  ${({ $isMobileScreen }) =>
    $isMobileScreen
      ? css`
          padding: ${SPACING.three}px;
        `
      : css`
          padding: ${SPACING.five}px 0;
        `};

  border-radius: ${BORDER_RADIUS.five}px;
  overflow: hidden;
  background-color: ${LAYER_COLORS.one};

  > span {
    ${FONT_CSS.body.three};
    color: ${TEXT_COLORS.two};

    a {
      color: ${TEXT_COLORS.three};
      text-decoration: none;
      transition: 0.2s all ease-in-out;

      > svg {
        transition: 0.2s all ease-in-out;
        stroke: ${TEXT_COLORS.three};
      }

      &:hover {
        color: ${TEXT_COLORS.one};

        > svg {
          stroke: ${TEXT_COLORS.one};
        }
      }
    }
  }
`;
